import React from "react";
import { Monitor, Smartphone, Tablet, ChefHat, LogOut, User, Cog } from "lucide-react";
import type { Staff } from "../types";
import { RESTAURANT_SETTINGS_ID } from "../constants";


type Store = {
  id: string;
  name: string;
  location: string;
  logo?: string | null;
  email?: string | null;
  phone?: string | null;
  status?: string | null;
};

interface ModeSelectionProps {
  currentStaff: Staff | null;
  onModeSelect: (mode: string) => void;
  onLogout: () => void;

  // Forkcast store lock
  stores: Store[];
  activeStoreId: string | null;
  onSelectStore: (storeId: string) => void;
}

const ModeSelection: React.FC<ModeSelectionProps> = ({
  currentStaff,
  onModeSelect,
  onLogout,
  stores = [],
  activeStoreId,
  onSelectStore,
}) => {
  const modes = [
    {
      id: "till",
      label: "Till / POS",
      icon: Monitor,
      color: "from-blue-600 to-blue-700",
      description: "Process orders and payments at the counter",
    },
    {
      id: "kds",
      label: "Kitchen Display",
      icon: ChefHat,
      color: "from-green-600 to-green-700",
      description: "View and manage orders in the kitchen",
    },
    {
      id: "handheld",
      label: "Handheld / Server",
      icon: Smartphone,
      color: "from-purple-600 to-purple-700",
      description: "Take orders on the restaurant floor",
    },
    {
      id: "kiosk",
      label: "Customer Kiosk",
      icon: Tablet,
      color: "from-orange-600 to-orange-700",
      description: "Self-service ordering for customers",
    },
  ];

  const activeStore = React.useMemo(() => {
    if (!activeStoreId) return null;
    return stores.find((s) => s.id === activeStoreId) ?? null;
  }, [stores, activeStoreId]);

  // Filter modes based on staff role (best-effort: only if currentStaff is real)
  let availableModes = modes.filter((mode) => {
    const role = (currentStaff?.role || "").toLowerCase();

    if (role === "kitchen") return mode.id === "kds";
    if (role === "server") return ["handheld", "kds"].includes(mode.id);

    // managers/cashiers/unknown -> all
    return true;
  });

  const canOpenSettings = (currentStaff?.role || "").toLowerCase() === "manager";

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 flex items-center justify-center p-8">
      <div className="bg-white rounded-3xl shadow-2xl p-12 w-full max-w-6xl">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="flex items-center justify-center mb-6">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 rounded-2xl">
              <User size={48} className="text-white" />
            </div>
          </div>

          <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-3">
            {activeStore ? `Welcome to ${activeStore.name}` : "Select a store"}
          </h1>

          {activeStore?.location && (
            <p className="text-gray-600 mb-4">{activeStore.location}</p>
          )}

          <p className="text-xl text-gray-600 mb-3">
            {currentStaff?.name ? `Hello ${currentStaff.name}, select your device mode` : "Select your device mode"}
          </p>

          <div className="flex items-center justify-center gap-3 flex-wrap">
            <div className="inline-flex items-center bg-gray-100 px-6 py-3 rounded-xl">
              <span className="text-sm font-medium text-gray-700 capitalize">
                Role: {currentStaff?.role || "staff"}
              </span>
            </div>

            {/* Store selector */}
            {stores.length > 1 && (
              <div className="inline-flex items-center bg-gray-100 px-4 py-3 rounded-xl">
                <label className="text-sm font-medium text-gray-700 mr-3">Store</label>
                <select
                  value={activeStoreId ?? ""}
                  onChange={(e) => {
  const v = e.target.value;
  if (!v) return; // don't pass "" through
  onSelectStore(v);
}}
                  className="bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm"
                >
                  <option value="" disabled>
                    Select a store
                  </option>
                  {stores.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {stores.length === 1 && stores[0] && (
              <div className="inline-flex items-center bg-gray-100 px-6 py-3 rounded-xl">
                <span className="text-sm font-medium text-gray-700">
                  Store: {stores[0].name}
                </span>
              </div>
            )}
          </div>

          {!activeStoreId && stores.length > 1 && (
            <p className="text-sm text-red-600 mt-4">
              Please select a store to continue.
            </p>
          )}
        </div>

        {/* Mode Selection Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {availableModes.map((mode) => {
            const Icon = mode.icon;
            const disabled = !activeStoreId;

            return (
              <button
                key={mode.id}
                onClick={() => onModeSelect(mode.id)}
                disabled={disabled}
                className={[
                  `group relative bg-gradient-to-br ${mode.color} p-8 rounded-2xl text-white shadow-xl transform transition-all duration-300 text-left`,
                  disabled ? "opacity-60 cursor-not-allowed" : "hover:shadow-2xl hover:scale-105",
                ].join(" ")}
                title={disabled ? "Select a store first" : undefined}
              >
                <div className="flex flex-col items-center text-center">
                  <div className="bg-white bg-opacity-20 p-6 rounded-2xl mb-6 group-hover:bg-opacity-30 transition-all duration-300">
                    <Icon size={48} />
                  </div>
                  <h3 className="text-2xl font-bold mb-3">{mode.label}</h3>
                  <p className="text-white text-opacity-90 leading-relaxed">{mode.description}</p>
                </div>

                <div className="absolute inset-0 bg-white bg-opacity-0 group-hover:bg-opacity-10 rounded-2xl transition-all duration-300"></div>
              </button>
            );
          })}
        </div>

        {/* Footer */}
        <div className="flex justify-center">
          <div className="flex space-x-4">
            {canOpenSettings && (
              <button
                onClick={() => alert("Settings page is POS-only for now. Hook this to Forkcast stores later.")}
                className="flex items-center space-x-3 px-8 py-4 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all duration-200 transform hover:scale-105 shadow-lg"
              >
                <Cog size={24} />
                <span className="font-semibold text-lg">Settings</span>
              </button>
            )}
            <button
              onClick={onLogout}
              className="flex items-center space-x-3 px-8 py-4 bg-gray-600 text-white rounded-xl hover:bg-gray-700 transition-all duration-200 transform hover:scale-105 shadow-lg"
            >
              <LogOut size={24} />
              <span className="font-semibold text-lg">Logout</span>
            </button>
          </div>
        </div>

        {/* Device Info */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-500">
            This device will be locked to your selected mode. To switch modes, logout and login again.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ModeSelection;
